/**
 * OAuth callback: GET /api/auth/callback?code=...&redirect=/
 *
 * The backend redirects here after Google/WorkOS OAuth.
 * We exchange the code by calling the backend directly from the browser
 * so the session cookie lands on the backend domain (SameSite=None; Secure).
 */
import { useState, useEffect } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { BACKEND_URL } from '../lib/api'
import { getCurrentUser } from '../lib/auth-client'
import { sanitizeRedirectPath } from '../lib/redirect'
import { aliasAndIdentifyFrom, identifyUser } from '../lib/mixpanel'

export const Route = createFileRoute('/api/auth/callback')({
  validateSearch: (search: Record<string, unknown>): { code?: string; redirect?: string; mpid?: string } => ({
    code: (search.code as string) || undefined,
    redirect: (search.redirect as string) || undefined,
    mpid: (search.mpid as string) || undefined,
  }),
  component: OAuthCallbackPage,
})

function OAuthCallbackPage() {
  const { code, redirect: redirectPath, mpid } = Route.useSearch()
  const [error, setError] = useState<string | null>(null)
  const safeRedirectPath = sanitizeRedirectPath(redirectPath)

  useEffect(() => {
    if (!code) {
      setError('Missing authorization code.')
      return
    }

    fetch(`${BACKEND_URL}/api/v1/auth/oauth/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
      credentials: 'include',
    })
      .then(async (res) => {
        if (res.ok) {
          // Check if user is school staff — redirect to school dashboard instead of family dashboard
          const user = await getCurrentUser()
          if (user) {
            const properties = {
              $email: user.email,
              ...(user.name ? { $name: user.name } : {}),
            }
            if (mpid) {
              aliasAndIdentifyFrom(user.id, mpid, properties)
            } else {
              identifyUser(user.id, properties)
            }
          }
          if (user?.tenantType === 'school' && user.tenantId) {
            window.location.href = `/school/${user.tenantId}`
          } else {
            window.location.href = safeRedirectPath
          }
        } else {
          setError('Authentication failed. Please try again.')
        }
      })
      .catch(() => {
        setError('Authentication failed. Please try again.')
      })
  }, [code, mpid, safeRedirectPath])

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-page-bg p-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-lg text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-3">Sign In Failed</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <a
            href="/login"
            className="inline-block px-6 py-3 bg-brand text-white rounded-xl font-medium hover:bg-brand-hover transition-colors"
          >
            Back to Login
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-page-bg p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-lg text-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand mx-auto mb-4" />
        <p className="text-gray-600">Completing sign in...</p>
      </div>
    </div>
  )
}
